import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, cacheGet, cacheSet, errText } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { TRACK_IMAGES, LESSON_IMAGES, HERO_IMG } from "@/lib/images";
import { toast } from "sonner";
import { CheckCircle2, Circle, XCircle, Clock, Award, Video, FileText, Upload, Loader2 } from "lucide-react";

const StatusPill = ({ state, label }) => {
  const styles = {
    done: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-700",
    todo: "bg-slate-100 text-slate-500",
  };
  const icons = { done: <CheckCircle2 size={14} />, pending: <Clock size={14} />, rejected: <XCircle size={14} />, todo: <Circle size={14} /> };
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles[state]}`}>{icons[state]} {label}</span>;
};

export default function TrackDetail() {
  const { id } = useParams();
  const [track, setTrack] = useState(cacheGet(`track_${id}`));
  const [enr, setEnr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { t, pick } = useLang();
  const navigate = useNavigate();

  const loadEnr = useCallback(() => {
    api.get("/me/enrollments").then((r) => {
      cacheSet("enrollments", r.data);
      setEnr(r.data.find((e) => e.track_id === id) || false);
    }).catch(() => {
      const cached = cacheGet("enrollments") || [];
      setEnr(cached.find((e) => e.track_id === id) || false);
    });
  }, [id]);

  useEffect(() => {
    api.get(`/tracks/${id}`).then((r) => { setTrack(r.data); cacheSet(`track_${id}`, r.data); }).catch(() => {});
    loadEnr();
  }, [id, loadEnr]);

  if (!track) return <p className="text-slate-500">Loading…</p>;

  const enroll = async () => {
    try {
      const { data } = await api.post(`/tracks/${id}/enroll`);
      setEnr(data);
      toast.success(t("enrolled"));
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tracks/${id}/video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("pending"));
      loadEnr();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const lessonsDone = enr ? enr.lessons_completed.length : 0;
  const allLessonsDone = lessonsDone >= track.lessons.length;
  const theoryState = enr?.exam_passed ? "done" : "todo";
  const videoState = !enr ? "todo" : enr.video_status === "approved" ? "done" : enr.video_status === "pending" ? "pending" : enr.video_status === "rejected" ? "rejected" : "todo";
  const hasVideo = track.components.includes("video");

  return (
    <div className="space-y-6">
      <div>
        <div className="relative rounded-2xl overflow-hidden mb-4 h-36 sm:h-48" data-testid="track-banner">
          <img src={TRACK_IMAGES[id] || HERO_IMG} alt={pick(track.title)} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-teal-950/80 to-transparent" />
          <h1 className="absolute bottom-3 left-4 right-4 font-heading text-2xl sm:text-3xl font-extrabold text-white">{pick(track.title)}</h1>
        </div>
        <p className="text-slate-500 mt-1">{pick(track.desc)}</p>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="component-checklist">
          <StatusPill state={theoryState} label={`${t("theory")} ${enr?.exam_passed ? "✓" : "✕"}`} />
          {hasVideo && <StatusPill state={videoState} label={`${t("videoPractical")} ${videoState === "done" ? "✓" : "✕"}`} />}
        </div>
      </div>

      {!enr && enr !== null && (
        <button data-testid="enroll-btn" onClick={enroll}
          className="w-full sm:w-auto h-12 px-8 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors">
          {t("enroll")}
        </button>
      )}

      {enr && (
        <>
          <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h2 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><FileText size={18} /> {t("lessons")} ({lessonsDone}/{track.lessons.length})</h2>
            <div className="space-y-2">
              {track.lessons.map((l, i) => {
                const done = enr.lessons_completed.includes(l.id);
                return (
                  <Link key={l.id} to={`/track/${id}/lesson/${l.id}`} data-testid={`lesson-link-${l.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/50 transition-colors">
                    <img src={LESSON_IMAGES[l.id] || TRACK_IMAGES[id] || HERO_IMG} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                    {done ? <CheckCircle2 className="text-emerald-600 shrink-0" size={20} /> : <Circle className="text-slate-300 shrink-0" size={20} />}
                    <span className={`font-medium ${done ? "text-slate-500" : "text-slate-800"}`}>{i + 1}. {pick(l.title)}</span>
                  </Link>
                );
              })}
            </div>
            <div className="pt-2">
              {enr.exam_passed ? (
                <StatusPill state="done" label={`${t("finalExam")}: ${enr.exam_score}%`} />
              ) : allLessonsDone ? (
                <button data-testid="take-exam-btn" onClick={() => navigate(`/track/${id}/exam`)}
                  className="h-12 px-6 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
                  {t("takeExam")}
                </button>
              ) : (
                <p className="text-sm text-slate-400">{t("examLocked")}</p>
              )}
            </div>
          </section>

          {hasVideo && (
            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <h2 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><Video size={18} /> {t("videoPractical")}</h2>
              <p className="text-sm text-slate-500">{t("videoInstructions")}</p>
              {videoState === "done" && <StatusPill state="done" label={t("approved")} />}
              {videoState === "pending" && <StatusPill state="pending" label={t("pending")} />}
              {(videoState === "todo" || videoState === "rejected") && (
                <label className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-teal-600 text-white font-semibold cursor-pointer hover:bg-teal-700 transition-colors">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                  {videoState === "rejected" ? t("rejected") : t("uploadVideo")}
                  <input data-testid="video-upload-input" type="file" accept="video/*" className="hidden" disabled={uploading}
                    onChange={(e) => uploadVideo(e.target.files?.[0])} />
                </label>
              )}
            </section>
          )}

          {enr.certificate_id && (
            <section className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <Award className="text-amber-500" size={36} />
              <div className="flex-1">
                <h2 className="font-heading font-bold text-lg text-slate-900">{t("certificate")}</h2>
                <p className="text-sm text-slate-500">{enr.certificate_id}</p>
              </div>
              <div className="flex gap-2">
                <Link data-testid="view-certificate-btn" to={`/certificate/${enr.certificate_id}`}
                  className="h-12 px-5 rounded-xl bg-slate-900 text-white font-semibold flex items-center hover:bg-slate-800">{t("viewCertificate")}</Link>
                <Link data-testid="apply-fscd-btn" to={`/apply/${id}`}
                  className="h-12 px-5 rounded-xl bg-emerald-600 text-white font-semibold flex items-center hover:bg-emerald-700">{t("applyToFscd")}</Link>
              </div>
            </section>
          )}
          {!enr.certificate_id && <p className="text-xs text-slate-400">{t("applyLocked")}</p>}
        </>
      )}
    </div>
  );
}
